// #2121 v2 - Tabla del flujo de derivación: bandeja tipo mail con bulk-select, indicador
// de antigüedad, fechas relativas, y acción única "Asignar/Re-derivar". Sin botón "Ver".
import React, { useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { Button, Badge, Spinner, Input } from 'reactstrap';
import { useTranslate } from 'react-translate';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionOpenAssignModal,
  actionGetAssignableDoctors,
  actionToggleSelect,
  actionSelectAllVisible,
  actionClearSelection,
  actionOpenHistory,
} from 'store/actions/deviceStudiesActions';

// Color por antigüedad: verde < 24h, amarillo 1-7d, rojo > 7d
const getAgeColor = (createdAt) => {
  if (!createdAt) return '#ccc';
  const hours = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  if (hours < 24)        return '#28a745';
  if (hours < 24 * 7)    return '#ffc107';
  return '#dc3545';
};

// Fecha relativa estilo email
const formatRelativeDate = (createdAt, locale = 'de') => {
  if (!createdAt) return '—';
  const m = moment(createdAt);
  const now = moment();
  if (m.isSame(now, 'day'))                return m.format('HH:mm');
  if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return moment.utc(0).locale(locale).from(now); // "ayer"
  if (m.isAfter(now.clone().subtract(7, 'days'))) return m.locale(locale).format('ddd');
  return m.format('DD.MM.YY');
};

// Color consistente por tipo de dispositivo (hash sencillo)
const TYPE_COLORS = ['#5e72e4', '#11cdef', '#fb6340', '#2dce89', '#f5365c', '#8965e0', '#ffd600'];
const colorForType = (type) => {
  if (!type) return '#999';
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return TYPE_COLORS[h % TYPE_COLORS.length];
};

const DeviceStudiesTable = ({ studies, loadingList }) => {
  const dispatch    = useDispatch();
  const t           = useTranslate('device-studies');
  const language    = useSelector((s) => s.userReducer?.language || 'de');
  const practice    = useSelector((s) => s.practicesReducer.practice);
  const selectedIds = useSelector((s) => s.deviceStudiesReducer.selectedIds);

  const allSelected = studies.length > 0 && selectedIds.length === studies.length;

  const toggleAll = () => {
    if (allSelected) dispatch(actionClearSelection());
    else dispatch(actionSelectAllVisible(studies.map((s) => String(s._id))));
  };

  const columns = useMemo(() => [
    {
      id:     'select',
      Header: () => (
        <Input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          style={{ position: 'static', margin: 0 }}
        />
      ),
      Cell: ({ row }) => {
        const id = String(row.original._id);
        const checked = selectedIds.includes(id);
        return (
          <Input
            type="checkbox"
            checked={checked}
            onChange={() => dispatch(actionToggleSelect(id))}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'static', margin: 0 }}
          />
        );
      },
      disableSortBy: true,
    },
    {
      id:     'age',
      Header: '',
      Cell:   ({ row }) => (
        <span
          title={moment(row.original.created_at).format('DD.MM.YYYY HH:mm')}
          style={{
            display:      'inline-block',
            width:        10,
            height:       10,
            borderRadius: '50%',
            background:   getAgeColor(row.original.created_at),
          }}
        />
      ),
      disableSortBy: true,
    },
    {
      id:     'kit_id',
      Header: t('kit-id'),
      Cell:     ({ row }) =>
        row.original && row.original.kit_id ? `${row.original.kit_id}` : '—',
      disableSortBy: true,
    },
    {
      accessor: 'created_at',
      Header:   t('col-date'),
      Cell:     ({ value }) => formatRelativeDate(value, language),
    },
    {
      accessor: 'patient_data',
      Header:   t('col-patient'),
      Cell:     ({ value }) =>
        value ? `${value.lastName}, ${value.firstName}` : '—',
    },
    {
      accessor: 'device_type',
      Header:   t('col-device-type'),
      Cell:     ({ value }) => value
        ? <Badge style={{ backgroundColor: colorForType(value), color: '#fff' }}>{value}</Badge>
        : <span className="text-muted">—</span>,
    },
    {
      accessor: 'assigned_doctor_name',
      Header:   t('col-doctor'),
      Cell:     ({ value, row }) => {
        const study = row.original;
        const count = (study.derivations && study.derivations.length) || 0;
        if (count === 0) {
          return <span className="text-muted font-italic">{t('status-unassigned')}</span>;
        }
        const openHistory = (e) => {
          e.stopPropagation();
          dispatch(actionGetAssignableDoctors(String(study._id), practice?._id));
          dispatch(actionOpenHistory(String(study._id)));
        };
        return (
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <span>{value || '—'}</span>
            <Button
              color="link"
              size="sm"
              className="p-0"
              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
              title={t('history-tooltip')}
              onClick={openHistory}
            >
              🕐 {count}
            </Button>
          </span>
        );
      },
    },
    {
      id:     'actions',
      Header: '',
      Cell:   ({ row }) => {
        const study  = row.original;
        const isAssigned = study.derivations && study.derivations.length > 0;
        const openAssign = () => {
          dispatch(actionOpenAssignModal(String(study._id)));
          dispatch(actionGetAssignableDoctors(String(study._id), practice?._id));
        };
        return (
          <Button
            color={isAssigned ? 'secondary' : 'primary'}
            size="sm"
            onClick={openAssign}
          >
            {isAssigned ? t('btn-rederive') : t('btn-assign')}
          </Button>
        );
      },
      disableSortBy: true,
    },
  ], [language, practice, t, dispatch, selectedIds, allSelected]);

  const data = useMemo(() => studies, [studies]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data, manualPagination: true },
    useSortBy,
    usePagination
  );

  if (loadingList) {
    return (
      <div className="text-center py-4">
        <Spinner />
      </div>
    );
  }

  if (!data.length) return null;

  return (
    <div className="table-responsive device-studies-table">
      <table {...getTableProps()} className="table table-hover">
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps && col.getSortByToggleProps())}>
                  {col.render('Header')}
                  {col.isSorted ? (col.isSortedDesc ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const hasDerivations = row.original.derivations && row.original.derivations.length > 0;
            const id = String(row.original._id);
            const isSelected = selectedIds.includes(id);
            return (
              <tr
                {...row.getRowProps()}
                className={isSelected ? 'table-active' : ''}
                style={{ fontWeight: hasDerivations ? 'normal' : 'bold' }}
              >
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} style={{ verticalAlign: 'middle' }}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DeviceStudiesTable;
// #2121 - End
